import type { HTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, RotateCw, type LucideIcon } from "lucide-react";

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface PageLayoutProps {
  children: ReactNode;
  /** Extra classes for <main>, merged onto the standard container and page rhythm. */
  className?: string;
  /** Extra classes for the outer shell (e.g. "flex flex-col"). */
  shellClassName?: string;
}

/**
 * The header/main/footer shell every public page repeats — including once per
 * loading / error / not-found branch, which is where most of the duplication was.
 */
export function PageLayout({ children, className, shellClassName }: PageLayoutProps) {
  return (
    <div className={cn("page-shell", shellClassName)}>
      <Header />
      {/* pt-4: breadcrumbs (which carry the desktop top spacing) are hidden on phones, so
          without it every phone title sat flush under the header. pb-12 is the only gap
          above the footer. */}
      <main className={cn("container pt-4 pb-12", className)}>{children}</main>
      <Footer />
    </div>
  );
}

interface ErrorStateProps {
  /** What failed, in the user's terms. */
  title: string;
  description?: string;
  /** Shows a "Повторить" button; pass the query's `refetch`. */
  onRetry?: () => void;
  children?: ReactNode;
}

/** Inline "failed to load" block for use inside a page that already has its shell. */
export function ErrorState({ title, description, onRetry, children }: ErrorStateProps) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <AlertCircle className="size-12 text-destructive" aria-hidden="true" />
      <p className="section-heading">{title}</p>
      {description && <p className="text-muted-foreground">{description}</p>}
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RotateCw className="size-4" />
          Повторить
        </Button>
      )}
      {children}
    </div>
  );
}

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  /** Usually one Button; rendered under the text. */
  action?: ReactNode;
  /** "h1" when the empty state is the whole page (empty cart, not found). */
  as?: "h1" | "h2";
  /** Extra props for the heading, e.g. an id/tabIndex to move focus to it. */
  titleProps?: HTMLAttributes<HTMLHeadingElement>;
}

/** The one layout for "nothing here": icon, title, one line of help, one action. */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  as: Heading = "h2",
  titleProps,
}: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center py-12 text-center">
      {Icon && <Icon className="mb-4 size-12 text-muted-foreground" aria-hidden="true" />}
      {/* As the page's h1 it takes the page-title role, so it matches every other title. */}
      <Heading
        {...titleProps}
        className={cn(Heading === "h1" ? "page-title" : "section-heading", titleProps?.className)}
      >
        {title}
      </Heading>
      {description && <p className="mt-2 max-w-md text-muted-foreground">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

interface PageErrorProps {
  /** What failed, in the user's terms. */
  title: string;
  onRetry?: () => void;
  backTo?: string;
  backLabel?: string;
}

/** Full-page error block, wrapped in the standard shell. */
export function PageError({
  title,
  onRetry,
  backTo = "/catalog",
  backLabel = "Вернуться в каталог",
}: PageErrorProps) {
  return (
    <PageLayout>
      <ErrorState title={title} onRetry={onRetry}>
        <Link
          to={backTo}
          className="inline-flex min-h-touch items-center text-primary-strong hover:underline"
        >
          {backLabel}
        </Link>
      </ErrorState>
    </PageLayout>
  );
}

interface PageNotFoundProps {
  title: string;
  backTo?: string;
  backLabel?: string;
}

/** "X not found" block, wrapped in the standard shell. */
export function PageNotFound({
  title,
  backTo = "/catalog",
  backLabel = "Вернуться в каталог",
}: PageNotFoundProps) {
  return (
    <PageLayout>
      <EmptyState
        as="h1"
        title={title}
        action={
          <Link
            to={backTo}
            className="inline-flex min-h-touch items-center text-primary-strong hover:underline"
          >
            {backLabel}
          </Link>
        }
      />
    </PageLayout>
  );
}
